import { useState, useCallback } from 'react'
import { message } from 'antd'
import { getGeneralPresignedUrls } from '../../../services/api/aiVideoGeneration'
import useTaskPoller from './useTaskPoller'

/**
 * Custom hook for handling file uploads using general presigned URLs.
 *
 * Supports various payload configurations:
 * - Single image: { file_type: "png" }
 * - Multiple videos: { file_count: 3, category: "video" }
 * - PDF to folder: { file_type: "pdf", folder: "documents" }
 * - Generic upload: { file_count: 1 }
 * - Custom expiry: { file_type: "mp4", expiry_seconds: 600 }
 * - Multiple images to folder: { file_count: 5, category: "image", folder: "thumbnails" }
 */
export const usePresignedUpload = ({ videoId } = {}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [currentAction, setCurrentAction] = useState('')

  const presignedUrlPoller = useTaskPoller({ pollInterval: 2000 })

  /**
   * Upload a single file to a presigned URL with progress tracking.
   * @param {File} file - The file to upload.
   * @param {string} presignedUrl - The presigned URL to upload to.
   * @param {string} fileId - Unique identifier for tracking progress.
   * @param {string} [contentType] - Content type header (auto-detected if not provided).
   * @returns {Promise<{success: boolean}>}
   */
  const uploadFileToPresignedUrl = useCallback(
    async (file, presignedUrl, fileId, contentType) => {
      // Auto-detect content type if not provided
      const finalContentType =
        contentType || file.type || 'application/octet-stream'

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            )
            setUploadProgress(prev => ({
              ...prev,
              [fileId]: percentComplete
            }))
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true })
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`))
          }
        })

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'))
        })

        xhr.open('PUT', presignedUrl)
        xhr.setRequestHeader('Content-Type', finalContentType)
        xhr.send(file)
      })
    },
    []
  )

  /**
   * Fetch presigned URLs from the API.
   * Handles both direct responses and async task polling.
   * @param {Object} payload - The request payload.
   * @returns {Promise<Object>} - The presigned URL data from the API.
   */
  const fetchPresignedUrls = useCallback(
    async payload => {
      try {
        setCurrentAction('generating_urls')
        const response = await getGeneralPresignedUrls(payload)

        const data = response.data

        // Case 1: Direct success
        if (data && data.status === 'success' && data.output) {
          return data.output
        }

        // Case 2: Async task, need to poll
        const taskId =
          data.task_id ||
          data.output?.task_id ||
          (data.status === 'pending' && data.id)

        if (taskId) {
          console.log(`Polling for presigned URLs (Task ID: ${taskId})...`)
          const result = await presignedUrlPoller.startPolling(taskId)
          return result.output
        }

        // Case 3: Direct data without explicit success status
        if (data && (data.urls || data.upload_urls || data.presigned_urls)) {
          return data
        }

        throw new Error('Failed to get presigned URLs: Invalid response')
      } catch (error) {
        console.error('Error fetching presigned URLs:', error)
        throw error
      } finally {
        setCurrentAction('')
      }
    },
    [presignedUrlPoller]
  )

  /**
   * Upload files using the general presigned URL API.
   *
   * @param {Object} options - Upload options.
   * @param {File|File[]} options.files - Single file or array of files to upload.
   * @param {string} [options.fileType] - File type (e.g., "png", "mp4", "pdf").
   * @param {string} [options.category] - Category ("image", "video").
   * @param {string} [options.folder] - Target folder name.
   * @param {number} [options.expirySeconds] - Custom expiry time for the presigned URL.
   * @param {string} [options.contentType] - Override content type for upload.
   * @returns {Promise<{success: boolean, urls: string[], error?: Error}>}
   */
  const uploadFiles = useCallback(
    async ({
      files,
      fileType,
      category,
      folder,
      expirySeconds,
      contentType
    }) => {
      // Normalize files to array
      const fileArray = Array.isArray(files) ? files : [files]

      if (fileArray.length === 0) {
        message.error('No files provided for upload')
        return {
          success: false,
          urls: [],
          error: new Error('No files provided')
        }
      }

      setIsUploading(true)
      setUploadProgress({})
      setCurrentAction('starting')

      try {
        // Build the payload for getGeneralPresignedUrls
        const payload = {}

        if (fileArray.length > 1) {
          payload.file_count = fileArray.length
        }

        if (fileType) {
          payload.file_type = fileType
        }

        if (category) {
          payload.category = category
        }

        if (folder) {
          payload.folder = folder
        }

        if (expirySeconds) {
          payload.expiry_seconds = expirySeconds
        }

        // Fetch presigned URLs
        const presignedData = await fetchPresignedUrls({
          ...payload,
          video_id: videoId
        })

        // The API may return urls in different formats depending on the request
        // Common formats:
        // - { urls: [{ upload_url, file_url }] }
        // - { upload_urls: [{ upload_url, file_url }] }
        // - { presigned_urls: [...] }
        // - Single file: { upload_url, file_url }
        let uploadInfos = []

        if (presignedData.urls && Array.isArray(presignedData.urls)) {
          uploadInfos = presignedData.urls
        } else if (
          presignedData.upload_urls &&
          Array.isArray(presignedData.upload_urls)
        ) {
          uploadInfos = presignedData.upload_urls
        } else if (
          presignedData.presigned_urls &&
          Array.isArray(presignedData.presigned_urls)
        ) {
          uploadInfos = presignedData.presigned_urls
        } else if (presignedData.upload_url) {
          // Single file case
          uploadInfos = [presignedData]
        }

        if (uploadInfos.length < fileArray.length) {
          throw new Error(
            `Not enough presigned URLs returned. Expected ${fileArray.length}, got ${uploadInfos.length}`
          )
        }

        // Upload files
        setCurrentAction('uploading')
        const uploadedUrls = []

        for (let i = 0; i < fileArray.length; i++) {
          const file = fileArray[i]
          const uploadInfo = uploadInfos[i]

          const uploadUrl = uploadInfo.upload_url || uploadInfo.presigned_url
          const fileUrl =
            uploadInfo.file_url || uploadInfo.url || uploadInfo.object_url

          if (!uploadUrl) {
            throw new Error(`No upload URL for file at index ${i}`)
          }

          const fileId = file.uid || file.name || `file_${i}`

          await uploadFileToPresignedUrl(file, uploadUrl, fileId, contentType)

          uploadedUrls.push(fileUrl)
        }

        setCurrentAction('')
        return { success: true, urls: uploadedUrls }
      } catch (error) {
        console.error('Error uploading files:', error)
        message.error(
          error.message || 'Failed to upload files. Please try again.'
        )
        return { success: false, urls: [], error }
      } finally {
        setIsUploading(false)
        setCurrentAction('')
      }
    },
    [fetchPresignedUrls, uploadFileToPresignedUrl]
  )

  /**
   * Reset upload state
   */
  const resetUpload = useCallback(() => {
    setIsUploading(false)
    setUploadProgress({})
    setCurrentAction('')
  }, [])

  return {
    // State
    isUploading,
    uploadProgress,
    currentAction,
    pollingStatus: presignedUrlPoller.status,

    // Actions
    uploadFiles,
    uploadFileToPresignedUrl,
    fetchPresignedUrls,
    resetUpload
  }
}

export default usePresignedUpload
